import React, { useEffect, useState } from "react";
import useApi from "../../../CustomHooks/useApi";

const SetCompanyConfigCard = ({ serviceType, editData, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [formData, setFormData] = useState({
    serviceType: serviceType || "AEPS",
    maxAgentCharge: "",
    maxTotalCommission: "",
    enableMerchantCommission: true,
    enableMdCommission: true,
    description: "",
  });

  useEffect(() => { setFormData((p) => ({ ...p, serviceType })); }, [serviceType]);

  useEffect(() => {
    if (editData) {
      setFormData({
        serviceType: editData.serviceType || serviceType,
        maxAgentCharge: editData.maxAgentCharge ?? "",
        maxTotalCommission: editData.maxTotalCommission ?? "",
        enableMerchantCommission: editData.enableMerchantCommission ?? true,
        enableMdCommission: editData.enableMdCommission ?? true,
        description: editData.description || "",
      });
    }
  }, [editData]);

  const { callApi: setGlobalConfig } = useApi("/commission/company-config/set", "POST");

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((p) => ({ ...p, [name]: type === "checkbox" ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setMessage({ type: "", text: "" });
      await setGlobalConfig({
        serviceType: formData.serviceType,
        maxAgentCharge: Number(formData.maxAgentCharge),
        maxTotalCommission: Number(formData.maxTotalCommission),
        enableMerchantCommission: formData.enableMerchantCommission,
        enableMdCommission: formData.enableMdCommission,
        description: formData.description,
      });
      setMessage({ type: "success", text: "Config saved successfully." });
      setTimeout(() => onClose(), 1200);
    } catch (err) {
      setMessage({ type: "error", text: err?.response?.data?.message || "Failed to save config." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-[#0A2A4F]/10 flex items-center justify-center text-[#0A2A4F] text-lg">⚙</div>
        <div>
          <h2 className="text-lg font-bold text-gray-900">{editData ? "Edit" : "Set"} Company Config</h2>
          <p className="text-xs text-gray-400">Global caps for <span className="font-semibold text-gray-600">{serviceType}</span></p>
        </div>
      </div>

      {message.text && (
        <div className={`mb-5 rounded-xl px-4 py-3 text-sm font-medium ${
          message.type === "success" ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-red-50 text-red-600 border border-red-200"
        }`}>
          {message.text}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">

        {/* Service type (read-only) */}
        <div>
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Service Type</label>
          <div className="bg-gray-50 rounded-xl px-4 py-3 text-sm text-gray-600 font-semibold">{formData.serviceType}</div>
        </div>

        {/* Caps */}
        <div className="grid grid-cols-2 gap-4">
          {[
            { label: "Max Agent Charge (₹)", name: "maxAgentCharge", placeholder: "e.g. 50" },
            { label: "Max Total Commission (₹)", name: "maxTotalCommission", placeholder: "e.g. 100" },
          ].map(({ label, name, placeholder }) => (
            <div key={name}>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">{label}</label>
              <input
                type="number"
                name={name}
                value={formData[name]}
                onChange={handleChange}
                required
                placeholder={placeholder}
                className="w-full bg-gray-50 rounded-xl px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-[#0A2A4F] focus:bg-white transition"
              />
            </div>
          ))}
        </div>

        {/* Description */}
        <div>
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Description (optional)</label>
          <textarea
            rows={3}
            name="description"
            value={formData.description}
            onChange={handleChange}
            placeholder="Add a note about this config..."
            className="w-full bg-gray-50 rounded-xl px-4 py-3 text-sm outline-none resize-none focus:ring-2 focus:ring-[#0A2A4F] focus:bg-white transition"
          />
        </div>

        {/* Toggles */}
        <div className="grid grid-cols-2 gap-3">
          {[
            { label: "Merchant Commission", name: "enableMerchantCommission" },
            { label: "MD Commission", name: "enableMdCommission" },
          ].map(({ label, name }) => (
            <label key={name} className={`flex items-center justify-between rounded-xl px-4 py-4 cursor-pointer border-2 transition ${formData[name] ? "border-emerald-300 bg-emerald-50" : "border-gray-200 bg-gray-50"}`}>
              <span className="text-sm font-semibold text-gray-700">{label}</span>
              <div className="flex items-center gap-2">
                <span className={`text-xs font-bold ${formData[name] ? "text-emerald-600" : "text-gray-400"}`}>{formData[name] ? "ON" : "OFF"}</span>
                <input type="checkbox" name={name} checked={formData[name]} onChange={handleChange} className="w-4 h-4 accent-emerald-600" />
              </div>
            </label>
          ))}
        </div>

        <button
          type="submit"
          disabled={loading}
          className={`w-full py-3.5 rounded-xl text-white font-bold text-sm transition ${loading ? "bg-gray-300 cursor-not-allowed" : "bg-[#0A2A4F] hover:bg-[#0d3562] shadow-sm"}`}
        >
          {loading ? "Saving..." : "Save Configuration"}
        </button>
      </form>
    </div>
  );
};

export default SetCompanyConfigCard;
